"use client";

import { useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import { PendingSubscription } from "@/lib/types";
import { useModalZIndex } from "@/lib/use-modal-z-index";

interface AuthModalProps {
  pending: PendingSubscription[];
  onClose: () => void;
}

export function AuthModal({ pending, onClose }: AuthModalProps) {
  const t = useTranslations("Auth");
  const tSub = useTranslations("Subscription");
  const zIndex = useModalZIndex();
  const pathname = usePathname();

  // Serialize subscriptions as JSON for the auth flow
  const params = new URLSearchParams();
  params.set("subscriptions", JSON.stringify(
    pending.map((p) => ({ type: p.type, id: p.targetId, name: p.targetName }))
  ));
  // Use first item's image for the signup page hero
  const firstImage = pending.find((p) => p.targetImage)?.targetImage;
  if (firstImage) params.set("subscribeImage", firstImage);
  params.set("redirect", pathname);

  const signupUrl = `/auth/signup?${params.toString()}`;
  const loginUrl = `/auth/login?${params.toString()}`;

  // Display text — event-focused when first item is a show
  const first = pending[0];
  const showItem = pending.find((p) => p.type === "show");

  const heading = showItem
    ? `${tSub("follow")} ${showItem.targetName.toUpperCase()}`
    : first.type === "artist"
      ? tSub("neverMissArtist", { name: first.targetName.toUpperCase() })
      : `${tSub("follow")} ${first.targetName.toUpperCase()}`;

  const description = showItem
    ? tSub("signUpNotifyShow")
    : first.type === "artist"
      ? tSub("signUpNotifyArtist", { name: first.targetName })
      : tSub("signUpNotifyVenue", { name: first.targetName });

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm border border-neutral-800 bg-surface px-8 py-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -top-1 -left-1 h-3 w-3 border-t border-l border-acid" />
        <div className="absolute -top-1 -right-1 h-3 w-3 border-t border-r border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        <h2 className="mb-2 text-center font-mono text-sm font-bold tracking-wider text-white">
          {heading}
        </h2>
        <p className="mb-6 text-center text-sm text-neutral-400">
          {description}
        </p>

        <a
          href={signupUrl}
          rel="nofollow"
          className="mb-3 block w-full rounded bg-acid py-2.5 text-center font-mono text-sm font-bold text-black transition-opacity hover:opacity-90"
        >
          {t("signUp")}
        </a>

        <p className="text-center font-mono text-xs text-neutral-500">
          {t("alreadyHaveAccount")}{" "}
          <a
            href={loginUrl}
            rel="nofollow"
            className="text-acid transition-colors hover:text-white"
          >
            {t("logIn")}
          </a>
        </p>
      </div>
    </div>
  );
}
